import { DesktopAppFrame } from "@/components/desktop/DesktopChrome";
import { ThemedText } from "@/components/themed-text";
import { BrandColors } from "@/constants/theme";
import { showAlert } from "@/lib/alert";
import { isDriverRole } from "@/lib/roles";
import bookingService, { Booking } from "@/lib/services/bookings";
import { showToast } from "@/lib/toast";
import { isLocationOnlyService } from "@/lib/validation/booking";
import BottomSheet, {
  BottomSheetModal,
  BottomSheetScrollView,
} from "@gorhom/bottom-sheet";
import Ionicons from "@react-native-vector-icons/ionicons";
import { useFocusEffect, useLocalSearchParams, useRouter } from "expo-router";
import React, { useCallback, useMemo, useRef, useState } from "react";
import {
  Linking,
  Platform,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  View,
} from "react-native";
import { GestureHandlerRootView } from "react-native-gesture-handler";

import { BackButton } from "@/components/booking/BackButton";
import { BackToHomeButton } from "@/components/booking/BackToHomeButton";
import { BookingHeaderCard } from "@/components/booking/BookingHeaderCard";
import { BookingNotFound } from "@/components/booking/BookingNotFound";
import { BookingVehicleDetailsLink } from "@/components/booking/BookingVehicleDetailsLink";
import { CustomerInfoCard } from "@/components/booking/CustomerInfoCard";
import { FloatingAddressCard } from "@/components/booking/FloatingAddressCard";
import { VerificationCodeModal } from "@/components/booking/VerificationCodeModal";
import { LiveTrackingMap } from "@/components/map/live-tracking-map";
import { Loader } from "@/components/ui/loader";
import { isGeofenceEnabled } from "@/constants";
import { useAuth } from "@/contexts/auth-context";
import {
  syncBookingFromWebSocket,
  useBookingTrack,
} from "@/hooks/queries/use-bookings-query";
import { useDesktopLayout } from "@/hooks/use-desktop-layout";
import { queryClient } from "@/lib/query-client";
import { locationTrackingService } from "@/lib/services/location-tracking";
import {
  isWithinStatusGeofence,
  recalculateBookingETA,
} from "@/lib/utils/map-utils";
import { useAuthStore } from "@/stores/use-auth-store";
import { useLocationStore } from "@/stores/use-location-store";
import { desktopStyles } from "@/styles/live-booking";
import { useSafeAreaInsets } from "react-native-safe-area-context";

interface DistanceTravel {
  duration: number;
  distance: number;
}

const pickupStatuses = ["accepted", "arrived", "pickup_inspection"];

export default function LiveDrivingRoute() {
  return (
    <DesktopAppFrame>
      <LiveDrivingScreen />
    </DesktopAppFrame>
  );
}

function LiveDrivingScreen() {
  const { theme } = useAuth();
  const { id } = useLocalSearchParams();
  const bookingId = Number(id);
  const hasBookingId = Number.isInteger(bookingId) && bookingId > 0;
  const mapRef = useRef<any>(null);
  const router = useRouter();
  const { user, setActiveBooking } = useAuthStore();
  const insets = useSafeAreaInsets();
  const desktop = useDesktopLayout();
  const [routeTravel, setRouteTravel] = useState<DistanceTravel | null>(null);
  const verificationSheetRef = useRef<BottomSheetModal>(null);
  const [isCompletingBooking, setIsCompletingBooking] = useState(false);
  const { data, isLoading: loading } = useBookingTrack(bookingId);
  const freshBooking = data?.booking;
  const booking = freshBooking ?? null;

  useFocusEffect(
    useCallback(() => {
      if (!hasBookingId) {
        return;
      }
      setActiveBooking(bookingId);
    }, [bookingId, hasBookingId, setActiveBooking]),
  );

  const coordinate = useLocationStore((state) => state.coordinate);

  const origin = useMemo(() => {
    if (!booking) return null;
    if (booking.status === "pending" || booking.status === "quoted") {
      return booking.from;
    }
    return coordinate ?? locationTrackingService.lastLocation ?? booking.from;
  }, [booking, coordinate]);

  const destination = useMemo(() => {
    if (!booking) return null;
    if (booking.status === "pending" || booking.status === "quoted") {
      return booking.to;
    }
    if (isLocationOnlyService(booking.service_type)) {
      return booking.from;
    }
    if (pickupStatuses.includes(booking.status)) {
      return booking.from;
    }
    return booking.to;
  }, [booking]);

  console.log({origin, destination});

  const estimatedTravel = useMemo(() => {
    const driverLocation = coordinate ?? locationTrackingService.lastLocation;
    if (!driverLocation || !destination) return null;
    return recalculateBookingETA(driverLocation, destination);
  }, [coordinate, destination]);

  const distanceTravelled = routeTravel ?? estimatedTravel;

  const applyBookingUpdate = (updatedBooking: Booking) => {
    syncBookingFromWebSocket(queryClient, updatedBooking);
  };

  // Bottom Sheet
  const bottomSheetRef = useRef<BottomSheet>(null);
  const snapPoints = useMemo(() => ["15%", "40%"], []);

  const locationOnly = booking
    ? isLocationOnlyService(booking.service_type)
    : false;

  const requiresGeofence = useMemo(() => {
    if (!booking || !isDriverRole(user?.role)) {
      return false;
    }

    if (locationOnly && booking.status === "accepted") {
      return true;
    }

    if (booking.status === "accepted") {
      return true;
    }

    return booking.status === "in_progress" && !locationOnly;
  }, [booking, user?.role, locationOnly]);

  const geofenceLabel = useMemo(() => {
    if (!booking) {
      return "destination";
    }

    if (locationOnly && booking.status === "accepted") {
      return "customer site";
    }

    if (pickupStatuses.includes(booking.status)) {
      return "pickup";
    }

    return "dropoff";
  }, [booking, locationOnly]);

  const withinGeofence =
    !isGeofenceEnabled || isWithinStatusGeofence(distanceTravelled);

  const renderGeofenceHint = () => {
    if (!requiresGeofence || withinGeofence) {
      return null;
    }

    return (
      <View style={styles.infoMessage}>
        <Ionicons name="navigate-outline" size={20} color="#FF9500" />
        <ThemedText
          style={[styles.infoMessageText, { color: "#FF9500" }]}
          type="small"
        >
          Move closer — you must be within 3 minutes of the {geofenceLabel} to
          continue.
        </ThemedText>
      </View>
    );
  };

  const handleCallCustomer = () => {
    if (booking?.customer?.phone) {
      Linking.openURL(`tel:${booking.customer.phone}`);
    }
  };

  const handleOpenChat = () => {
    router.push({
      pathname: "/bookings/[id]/chat",
      params: {
        id: bookingId,
        user: JSON.stringify(booking?.customer),
        reload: "false",
      },
    });
  };

  const handleOpenNavigation = () => {
    if (!booking) return;

    let latitude: number;
    let longitude: number;

    if (booking.status === "accepted" || booking.status === "arrived") {
      latitude = booking.from.latitude;
      longitude = booking.from.longitude;
    } else {
      latitude = booking.to.latitude;
      longitude = booking.to.longitude;
    }

    const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${latitude},${longitude}`;
    const appleMapsUrl = `http://maps.apple.com/?daddr=${latitude},${longitude}`;

    if (Platform.OS === "ios") {
      showAlert("Open Navigation", "Choose a map application", [
        {
          text: "Apple Maps",
          onPress: () => Linking.openURL(appleMapsUrl),
        },
        {
          text: "Google Maps",
          onPress: () => Linking.openURL(googleMapsUrl),
        },
        {
          text: "Cancel",
          style: "cancel",
        },
      ]);
    } else {
      Linking.openURL(googleMapsUrl);
    }
  };

  const handleMarkAsArrived = () => {
    showAlert(
      "Mark as Arrived",
      "Confirm that you have arrived at the pickup location?",
      [
        { text: "Cancel", style: "cancel" },
        {
          text: "Yes, I've Arrived",
          onPress: async () => {
            try {
              const updatedBooking = await bookingService.markAsArrived(
                bookingId,
              );
              applyBookingUpdate(updatedBooking);
              showToast.success("Marked as arrived successfully");
            } catch (error: any) {
              showToast.error(
                error.response?.data?.message || "Failed to mark as arrived",
              );
            }
          },
        },
      ],
    );
  };

  const handleStartRide = () => {
    showAlert("Start Ride", "Are you ready to start this ride?", [
      { text: "Cancel", style: "cancel" },
      {
        text: "Start Ride",
        onPress: async () => {
          try {
            const updatedBooking = await bookingService.startRide(bookingId);
            applyBookingUpdate(updatedBooking);
            showToast.success("Ride started successfully");
          } catch (error: any) {
            showToast.error(
              error.response?.data?.message || "Failed to start ride",
            );
          }
        },
      },
    ]);
  };

  const handleMarkAsDelivered = () => {
    showAlert(
      "Mark as Delivered",
      "Confirm that the vehicle has been delivered?",
      [
        { text: "Cancel", style: "cancel" },
        {
          text: "Yes, Delivered",
          onPress: async () => {
            try {
              const updatedBooking = await bookingService.markAsDelivered(
                bookingId,
              );
              applyBookingUpdate(updatedBooking);
              showToast.success("Marked as delivered successfully");
            } catch (error: any) {
              showToast.error(
                error.response?.data?.message || "Failed to mark as delivered",
              );
            }
          },
        },
      ],
    );
  };

  const handleCompleteBooking = () => {
    verificationSheetRef.current?.present();
  };

  const submitCompletion = async (verificationCode: string) => {
    try {
      setIsCompletingBooking(true);
      const updatedBooking = await bookingService.completeBooking(
        bookingId,
        verificationCode,
      );
      applyBookingUpdate(updatedBooking);
      verificationSheetRef.current?.dismiss();
      showToast.success("Booking completed successfully");
      useAuthStore.getState().setActiveBooking(null);
      router.push(`/rate-booking/${bookingId}`);
    } catch (error: any) {
      showToast.error(
        error.response?.data?.message || "Failed to complete booking",
      );
    } finally {
      setIsCompletingBooking(false);
    }
  };

  const handleRateBooking = () => {
    showAlert(
      "Rate Booking",
      "Thank you for your booking! Please rate your experience.",
      [
        { text: "No", style: "cancel" },
        {
          text: "Yes, Rate",
          onPress: async () => {
            try {
              router.replace(`/rate-booking/${bookingId}`);
            } catch (error: any) {
              showToast.error(
                error.response?.data?.message || "Failed to complete booking",
              );
            }
          },
        },
      ],
    );
  };

  if (loading) {
    return <Loader />;
  }

  if (!booking) {
    return <BookingNotFound />;
  }

  const sheetContent = (
    <>
      {/* Header Section */}
      <BookingHeaderCard
        scheduledAt={booking.scheduled_at}
        serviceTypeLabel={booking.service_type_label}
        statusLabel={booking.status_label}
        status={booking.status}
        paymentMethod={booking.payment_method}
        distanceTravelled={distanceTravelled}
        userRole={user?.role}
        showNavigation={!!destination}
        onNavigate={handleOpenNavigation}
      />

      {/* Actions Section */}
      <View style={styles.actionsSection}>
        {(() => {
          const locationOnly = isLocationOnlyService(booking.service_type);

          if (locationOnly) {
            // ── Location-only flow ──────────────────────────────────────
            // accepted → (start job directly (no inspection) / wait for payment) → in_progress → completed
            return (
              <>
                {/* ACCEPTED: wait for payment or start jobs*/}
                {isDriverRole(user?.role) &&
                  booking.status === "accepted" && (
                    <>
                      {!booking.payment ||
                      (booking.payment.status !== "authorized" &&
                        booking.payment.status !== "captured") ? (
                        <View
                          style={[
                            styles.infoMessage,
                            { backgroundColor: theme.background },
                          ]}
                        >
                          <Ionicons
                            name="time"
                            size={20}
                            color={theme.text}
                          />
                          <ThemedText
                            style={[styles.infoMessageText]}
                            type="small"
                          >
                            Waiting for customer payment...
                          </ThemedText>
                        </View>
                      ) : (
                        <>
                          {renderGeofenceHint()}
                          <TouchableOpacity
                            style={[
                              styles.actionButton,
                              styles.startRideButton,
                              !withinGeofence &&
                                styles.actionButtonDisabled,
                            ]}
                            onPress={handleStartRide}
                            disabled={!withinGeofence}
                          >
                            <Ionicons
                              name="play-circle"
                              size={20}
                              color="#fff"
                            />
                            <ThemedText
                              style={styles.actionButtonText}
                              type="defaultSemiBold"
                            >
                              Start Job
                            </ThemedText>
                          </TouchableOpacity>
                        </>
                      )}
                    </>
                  )}

                {/* IN_PROGRESS: complete directly (no delivery inspection) */}
                {isDriverRole(user?.role) &&
                  booking.status === "in_progress" && (
                    <>
                      <TouchableOpacity
                        style={[styles.actionButton, styles.completeButton]}
                        onPress={handleCompleteBooking}
                      >
                        <Ionicons
                          name="checkmark-circle"
                          size={20}
                          color="#fff"
                        />
                        <ThemedText
                          style={styles.actionButtonText}
                          type="defaultSemiBold"
                        >
                          Complete Job
                        </ThemedText>
                      </TouchableOpacity>
                    </>
                  )}
              </>
            );
          }

          // ── A-to-B flow ─────────────────────────────────────────────
          return (
            <>
              {/* ACCEPTED: wait for payment or mark arrived */}
              {user?.role === "driver" && booking.status === "accepted" && (
                <>
                  {!booking.payment ||
                  (booking.payment.status !== "authorized" &&
                    booking.payment.status !== "captured") ? (
                    <View style={styles.infoMessage}>
                      <Ionicons name="time" size={20} color="#FF9500" />
                      <ThemedText
                        style={[
                          styles.infoMessageText,
                          { color: "#FF9500" },
                        ]}
                        type="small"
                      >
                        Waiting for customer payment...
                      </ThemedText>
                    </View>
                  ) : (
                    <>
                      {renderGeofenceHint()}
                      <TouchableOpacity
                        style={[
                          styles.actionButton,
                          styles.startRideButton,
                          !withinGeofence && styles.actionButtonDisabled,
                        ]}
                        onPress={handleMarkAsArrived}
                        disabled={!withinGeofence}
                      >
                        <Ionicons name="location" size={20} color="#fff" />
                        <ThemedText
                          style={styles.actionButtonText}
                          type="defaultSemiBold"
                        >
                          Mark as Arrived
                        </ThemedText>
                      </TouchableOpacity>
                    </>
                  )}
                </>
              )}

              {/* ARRIVED: pickup inspection (full add-on or short 4-angle photos) */}
              {isDriverRole(user?.role) && booking.status === "arrived" && (
                <TouchableOpacity
                  style={[styles.actionButton, styles.inspectionButton]}
                  onPress={() =>
                    router.push(
                      booking.inspection_required
                        ? `/bookings/${bookingId}/vehicle-inspection?bookingId=${bookingId}&type=pickup&inspectionTier=${booking.inspection_tier ?? "basic"}`
                        : `/bookings/${bookingId}/vehicle-inspection?bookingId=${bookingId}&type=pickup&mode=short`,
                    )
                  }
                >
                  <Ionicons
                    name={booking.inspection_required ? "clipboard" : "camera"}
                    size={20}
                    color="#fff"
                  />
                  <ThemedText
                    style={styles.actionButtonText}
                    type="defaultSemiBold"
                  >
                    {booking.inspection_required
                      ? "Start Pickup Inspection"
                      : "Take Pickup Photos"}
                  </ThemedText>
                </TouchableOpacity>
              )}

              {/* PICKUP_INSPECTION: start ride */}
              {isDriverRole(user?.role) &&
                booking.status === "pickup_inspection" && (
                  <TouchableOpacity
                    style={[styles.actionButton, styles.startRideButton]}
                    onPress={handleStartRide}
                  >
                    <Ionicons name="play-circle" size={20} color="#fff" />
                    <ThemedText
                      style={styles.actionButtonText}
                      type="defaultSemiBold"
                    >
                      Start Ride
                    </ThemedText>
                  </TouchableOpacity>
                )}

              {/* IN_PROGRESS: mark as delivered */}
              {isDriverRole(user?.role) &&
                booking.status === "in_progress" && (
                  <>
                    {renderGeofenceHint()}
                    <TouchableOpacity
                      style={[
                        styles.actionButton,
                        styles.completeButton,
                        !withinGeofence && styles.actionButtonDisabled,
                      ]}
                      onPress={handleMarkAsDelivered}
                      disabled={!withinGeofence}
                    >
                      <Ionicons
                        name="checkmark-done"
                        size={20}
                        color="#fff"
                      />
                      <ThemedText
                        style={styles.actionButtonText}
                        type="defaultSemiBold"
                      >
                        Mark as Delivered
                      </ThemedText>
                    </TouchableOpacity>
                  </>
                )}

              {/* DELIVERED: delivery inspection (full add-on or short 4-angle photos) */}
              {isDriverRole(user?.role) && booking.status === "delivered" && (
                <TouchableOpacity
                  style={[styles.actionButton, styles.inspectionButton]}
                  onPress={() =>
                    router.push(
                      booking.inspection_required
                        ? `/bookings/${bookingId}/vehicle-inspection?bookingId=${bookingId}&type=delivery&inspectionTier=${booking.inspection_tier ?? "basic"}`
                        : `/bookings/${bookingId}/vehicle-inspection?bookingId=${bookingId}&type=delivery&mode=short`,
                    )
                  }
                >
                  <Ionicons
                    name={booking.inspection_required ? "clipboard" : "camera"}
                    size={20}
                    color="#fff"
                  />
                  <ThemedText
                    style={styles.actionButtonText}
                    type="defaultSemiBold"
                  >
                    {booking.inspection_required
                      ? "Start Delivery Inspection"
                      : "Take Delivery Photos"}
                  </ThemedText>
                </TouchableOpacity>
              )}

              {/* DELIVERY_INSPECTION: complete booking */}
              {isDriverRole(user?.role) &&
                booking.status === "delivery_inspection" && (
                  <TouchableOpacity
                    style={[styles.actionButton, styles.completeButton]}
                    onPress={handleCompleteBooking}
                  >
                    <Ionicons
                      name="checkmark-circle"
                      size={20}
                      color="#fff"
                    />
                    <ThemedText
                      style={styles.actionButtonText}
                      type="defaultSemiBold"
                    >
                      Complete Booking
                    </ThemedText>
                  </TouchableOpacity>
                )}
            </>
          );
        })()}

        {booking.status === "cancelled" && <BackToHomeButton />}

        {/* Completed — rate booking (all service types) */}
        {booking.status === "completed" && booking?.user_rating && (
          <BackToHomeButton />
        )}

        {booking.status === "completed" && booking?.user_rating == null && (
          <TouchableOpacity
            style={[styles.actionButton, styles.completeButton]}
            onPress={handleRateBooking}
          >
            <Ionicons name="checkmark-circle" size={20} color="#fff" />
            <ThemedText
              style={styles.actionButtonText}
              type="defaultSemiBold"
            >
              Rate Booking
            </ThemedText>
          </TouchableOpacity>
        )}
      </View>

      {/* Customer Info */}
      {user?.role === "driver" && booking?.customer && (
        <CustomerInfoCard
          name={booking.customer.name}
          roleLabel="Customer"
          subtitle="Customer"
          variant="detail"
          avatar={booking.customer.avatar}
          rating={booking.customer.reputation_score}
          loyaltyTier={booking.customer.loyalty_tier}
          isVerified={booking.customer.is_top_rated}
          onChat={booking?.driver ? handleOpenChat : undefined}
          onCall={handleCallCustomer}
          showActions={
            booking?.payment &&
            (booking?.payment?.status === "authorized" ||
              booking?.payment?.status === "captured")
              ? true
              : false
          }
        />
      )}

      {/* Vehicle details */}
      <BookingVehicleDetailsLink booking={booking} />
    </>
  );

  return (
    <GestureHandlerRootView style={styles.container}>
      {/* Map */}
      <View style={styles.mapContainer}>
        {origin && destination && (
          <LiveTrackingMap
            mapRef={mapRef}
            pickupLocation={origin}
            dropoffLocation={destination}
            onDistanceTravelled={(travel) => {
              setRouteTravel(travel);
            }}
            showUserLocation={false}
          />
        )}
        {/* Back Button */}
        <BackButton />

        {/* Floating Addresses */}
        {desktop ? (
          <View pointerEvents="box-none" style={desktopStyles.desktopAddressWrap}>
            <FloatingAddressCard
              fromAddress={booking.from_address ?? ""}
              toAddress={booking.to_address ?? ""}
              topInset={0}
              serviceType={booking.service_type}
              style={desktopStyles.desktopAddressCard}
            />
          </View>
        ) : (
          <FloatingAddressCard
            fromAddress={booking.from_address ?? ""}
            toAddress={booking.to_address ?? ""}
            topInset={insets.top}
            serviceType={booking.service_type}
          />
        )}
      </View>

      {desktop ? (
        <View pointerEvents="box-none" style={desktopStyles.desktopPanelWrap}>
          <View
            style={[
              desktopStyles.desktopPanel,
              { backgroundColor: theme.background, borderColor: theme.border },
            ]}
          >
            <ScrollView contentContainerStyle={desktopStyles.desktopPanelContent}>
              {sheetContent}
            </ScrollView>
          </View>
        </View>
      ) : (
        <BottomSheet
          ref={bottomSheetRef}
          index={1}
          snapPoints={snapPoints}
          backgroundStyle={[
            styles.bottomSheetBackground,
            { backgroundColor: theme.background },
          ]}
          handleIndicatorStyle={styles.bottomSheetHandle}
        >
          <BottomSheetScrollView
            contentContainerStyle={styles.bottomSheetContent}
          >
            {sheetContent}
          </BottomSheetScrollView>
        </BottomSheet>
      )}

      <VerificationCodeModal
        ref={verificationSheetRef}
        onClose={() => verificationSheetRef.current?.dismiss()}
        onSubmit={submitCompletion}
        isSubmitting={isCompletingBooking}
      />
    </GestureHandlerRootView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#f5f5f5",
  },
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  loadingText: {
    marginTop: 10,
    fontSize: 16,
    color: "#666",
  },
  primaryButton: {
    backgroundColor: BrandColors.primary,
    paddingHorizontal: 32,
    paddingVertical: 14,
    borderRadius: 12,
    marginBottom: 12,
    width: "80%",
    alignItems: "center",
  },
  primaryButtonText: {
    color: "white",
    fontSize: 16,
    fontWeight: "600",
  },
  secondaryButton: {
    backgroundColor: "transparent",
    paddingHorizontal: 32,
    paddingVertical: 14,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: BrandColors.primary,
    width: "80%",
    alignItems: "center",
  },
  secondaryButtonText: {
    color: BrandColors.primary,
    fontSize: 16,
    fontWeight: "600",
  },
  mapContainer: {
    flex: 1,
    position: "relative",
  },
  backButton: {
    position: "absolute",
    left: 20,
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: "white",
    justifyContent: "center",
    alignItems: "center",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.15,
    shadowRadius: 4,
    elevation: 4,
    zIndex: 10,
  },
  bottomSheetBackground: {
    backgroundColor: "#0b1120",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: -2 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 10,
  },
  bottomSheetHandle: {
    backgroundColor: "#475569",
    width: 40,
  },
  bottomSheetContent: {
    padding: 24,
    paddingBottom: 40,
  },
  etaDivider: {
    width: 1,
    height: 12,
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    marginHorizontal: 12,
  },
  etaValue: {
    fontSize: 14,
    fontWeight: "600",
    color: "#f8fafc",
  },
  etaLabel: {
    fontSize: 10,
    color: "rgba(255, 255, 255, 0.4)",
    marginLeft: 2,
    fontWeight: "600",
  },
  actionsSection: {
    marginBottom: 0,
  },
  actionButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 16,
    padding: 18,
    marginBottom: 12,
    gap: 12,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.2,
    shadowRadius: 8,
    elevation: 6,
  },
  actionButtonText: {
    fontSize: 16,
    color: "#fff",
    fontWeight: "600",
  },
  actionButtonDisabled: {
    opacity: 0.45,
  },
  payButton: {
    backgroundColor: BrandColors.success,
  },
  startRideButton: {
    backgroundColor: BrandColors.primary,
  },
  completeButton: {
    backgroundColor: BrandColors.success,
  },
  cancelButton: {
    backgroundColor: "#FF3B30",
  },
  inspectionButton: {
    backgroundColor: BrandColors.primary,
  },
  infoMessage: {
    flexDirection: "row",
    alignItems: "center",
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    gap: 12,
  },
  infoMessageText: {
    flex: 1,
    fontSize: 14,
    lineHeight: 20,
  },
});
